import React, { useState, useRef, useEffect, useCallback } from 'react';
import { optimizedImageUrl } from '../../../utils/imageUrl';
import { useNavigate } from 'react-router-dom';
import { useAuth, useNotifications } from '../../../context';
import searchService from '../../../services/search';
import { SECTIONS } from '../../../config/sections';
import { getNotificationRoute } from '../../../utils/notificationRoute';
// import LanguageSelector from '../LanguageSelector'; // Disabled until multi-language support is ready
import './ContentHeader.css';

// Helper to get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Helper to format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

// Map notification types to icon and color
const getNotificationStyle = (notificationType) => {
  const styles = {
    team_invitation: { icon: 'group_add', type: 'info' },
    invitation_accepted: { icon: 'check_circle', type: 'success' },
    member_joined: { icon: 'person_add', type: 'success' },
    member_left: { icon: 'person_remove', type: 'warning' },
    role_changed: { icon: 'swap_horiz', type: 'info' },
    affirmation: { icon: 'volunteer_activism', type: 'success' },
    recording_like: { icon: 'thumb_up', type: 'info' },
    recording_comment: { icon: 'comment', type: 'info' },
    question_completed: { icon: 'task_alt', type: 'success' },
    chat_message: { icon: 'chat', type: 'info' },
    general: { icon: 'info', type: 'info' },
  };
  return styles[notificationType] || styles.general;
};

// Result type icons and labels
const RESULT_TYPE_META = {
  user: { icon: 'person_search', label: 'Users' },
  member: { icon: 'person', label: 'People' },
  team: { icon: 'groups', label: 'Teams' },
  channel: { icon: 'tag', label: 'Channels' },
  message: { icon: 'chat_bubble_outline', label: 'Messages' },
  section: { icon: 'assignment', label: 'Sections' },
};

// Search sections client-side
const searchSections = (query) => {
  const q = query.toLowerCase();
  return SECTIONS.flatMap((section) => {
    const results = [];
    // Match section title/description
    if (section.title.toLowerCase().includes(q) || section.description?.toLowerCase().includes(q)) {
      results.push({
        type: 'section',
        id: section.id,
        title: section.title,
        subtitle: section.description?.substring(0, 60) || '',
        questionId: section.questionIds[0],
      });
    }
    // Match individual question IDs (e.g. "Q10A", "q11")
    for (const qid of section.questionIds) {
      if (qid.toLowerCase().includes(q)) {
        results.push({
          type: 'section',
          id: `${section.id}-${qid}`,
          title: qid,
          subtitle: section.title,
          questionId: qid,
        });
      }
    }
    return results;
  });
};

const ContentHeader = ({ onMenuClick, showMenuButton = true }) => {
  const handleMenuClick = onMenuClick || (() => {
    window.dispatchEvent(new CustomEvent('open-sidebar'));
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Search state
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, isLoading } = useNotifications();
  const userName = user?.display_name || user?.email?.split('@')[0] || 'User';
  const userRole = user?.is_hcw ? 'Healthcare Worker' : 'Regular User';
  const isPaid = user?.subscription_tier === 'full';

  // Debounced search
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults(null);
      setShowSearchDropdown(false);
      return;
    }
    setSearchLoading(true);
    setShowSearchDropdown(true);
    try {
      const data = await searchService.quickSearch(query);
      // Merge client-side section results
      const sectionResults = searchSections(query);
      if (sectionResults.length > 0) {
        data.results = [...(data.results || []), ...sectionResults.slice(0, 2)];
        data.total = (data.total || 0) + sectionResults.length;
        if (!data.counts) data.counts = {};
        data.counts.section = sectionResults.length;
      }
      setSearchResults(data);
    } catch {
      // Still show section results even if API fails
      const sectionResults = searchSections(query);
      if (sectionResults.length > 0) {
        setSearchResults({
          results: sectionResults.slice(0, 5),
          total: sectionResults.length,
          counts: { section: sectionResults.length },
        });
      } else {
        setSearchResults(null);
      }
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    clearTimeout(searchTimerRef.current);
    if (value.trim().length >= 2) {
      searchTimerRef.current = setTimeout(() => performSearch(value.trim()), 300);
    } else {
      setSearchResults(null);
      setShowSearchDropdown(false);
    }
  };

  // Close search dropdown on outside click
  useEffect(() => {
    if (!showSearchDropdown) return;
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSearchDropdown]);

  // Cleanup timer
  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchDropdown(false);
    }
  };

  const handleSearchToggle = () => {
    setSearchExpanded(!searchExpanded);
    if (searchExpanded) {
      setSearchQuery('');
      setSearchResults(null);
      setShowSearchDropdown(false);
    }
  };

  const handleResultClick = (result) => {
    setShowSearchDropdown(false);
    setSearchQuery('');
    if (result.type === 'user') {
      navigate(`/user/${result.id}`);
    } else if (result.type === 'team') {
      navigate(`/team/${result.id}`);
    } else if (result.type === 'member') {
      navigate(`/team/${result.team_id}/member/${result.id}`);
    } else if (result.type === 'channel') {
      navigate(`/team/${result.team_id}/channels/${result.id}`);
    } else if (result.type === 'message') {
      navigate(`/team/${result.team_id}/channels/${result.channel_id}?msg=${result.id}`);
    } else if (result.type === 'section') {
      navigate(`/questionnaire/${result.questionId}`);
    }
  };

  const handleSeeMore = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchDropdown(false);
    }
  };

  const renderSearchDropdown = () => {
    if (!showSearchDropdown) return null;

    return (
      <>
        <div className="search-dropdown__backdrop" onClick={() => setShowSearchDropdown(false)} />
        <div className="search-dropdown">
          {searchLoading ? (
            <div className="search-dropdown__loading">
              <div className="loading-spinner" />
              <span>Searching...</span>
            </div>
          ) : searchResults && searchResults.results?.length > 0 ? (
            <>
              <div className="search-dropdown__list">
                {searchResults.results.map((result) => {
                  const meta = RESULT_TYPE_META[result.type] || RESULT_TYPE_META.member;
                  return (
                    <button
                      key={`${result.type}-${result.id}`}
                      className="search-dropdown__item"
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="search-dropdown__item-icon">
                        {result.avatar ? (
                          <img src={result.avatar} alt="" />
                        ) : (
                          <span className="material-icons">{meta.icon}</span>
                        )}
                      </div>
                      <div className="search-dropdown__item-content">
                        <span className="search-dropdown__item-title">{result.title}</span>
                        <span className="search-dropdown__item-subtitle">{result.subtitle}</span>
                      </div>
                      <span className="search-dropdown__item-type">{meta.label}</span>
                    </button>
                  );
                })}
              </div>
              {searchResults.total > 5 && (
                <button className="search-dropdown__see-more" onClick={handleSeeMore}>
                  See all {searchResults.total} results
                </button>
              )}
            </>
          ) : searchResults ? (
            <div className="search-dropdown__empty">
              <span className="material-icons">search_off</span>
              <span>No results found</span>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <header className={`content-header ${searchExpanded ? 'content-header--search-expanded' : ''}`}>
      <div className="content-header__left">
        {showMenuButton && (
          <button
            className="content-header__menu-btn"
            onClick={handleMenuClick}
            aria-label="Open menu"
          >
            <span className="material-icons">menu</span>
          </button>
        )}

        {/* Search Bar - Desktop */}
        <div className="content-header__search-wrapper" ref={searchRef}>
          <form className="content-header__search content-header__search--desktop" onSubmit={handleSearch}>
            <span className="material-icons content-header__search-icon">search</span>
            <input
              type="text"
              placeholder="Search users, teams, channels..."
              value={searchQuery}
              onChange={handleSearchInput}
              onFocus={() => { if (searchResults) setShowSearchDropdown(true); }}
              className="content-header__search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="content-header__search-clear"
                onClick={() => { setSearchQuery(''); setSearchResults(null); setShowSearchDropdown(false); }}
              >
                <span className="material-icons">close</span>
              </button>
            )}
          </form>
          {renderSearchDropdown()}
        </div>
      </div>

      <div className="content-header__right">
        {/* Search Icon - Mobile/Tablet */}
        <button
          className="content-header__icon-btn content-header__search-toggle"
          onClick={handleSearchToggle}
          aria-label="Search"
        >
          <span className="material-icons">{searchExpanded ? 'close' : 'search'}</span>
        </button>

        {/* Notification Bell */}
        <div className="content-header__notifications" data-tour="bell">
          <button
            className="content-header__icon-btn"
            onClick={() => {
              const isOpening = !showNotifications;
              setShowNotifications(isOpening);
              if (isOpening && unreadCount > 0) {
                markAllAsRead();
              }
            }}
            aria-label="Notifications"
          >
            <span className="material-icons">notifications_none</span>
            {unreadCount > 0 && (
              <span className="content-header__badge">{unreadCount}</span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <>
              <div
                className="content-header__dropdown-backdrop"
                onClick={() => setShowNotifications(false)}
              />
              <div className="content-header__dropdown">
                <div className="dropdown__header">
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <button className="dropdown__action" onClick={() => markAllAsRead()}>
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="dropdown__list">
                  {isLoading ? (
                    <div className="dropdown__empty">
                      <span className="material-icons">hourglass_empty</span>
                      <p>Loading...</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    notifications.slice(0, 10).map((notification) => {
                      const style = getNotificationStyle(notification.notification_type);
                      return (
                        <div
                          key={notification.id}
                          className={`dropdown__item ${!notification.is_read ? 'dropdown__item--unread' : ''}`}
                          onClick={() => {
                            if (!notification.is_read) markAsRead(notification.id);
                            setShowNotifications(false);

                            // Team invitation → notifications page, where the
                            // accept/decline modal validates the token and opens.
                            if (notification.notification_type === 'team_invitation') {
                              navigate('/notifications');
                              return;
                            }

                            const route = getNotificationRoute(notification);
                            // Fallback to the full list when there's no specific target.
                            navigate(route ? route.path : '/notifications', route?.state ? { state: route.state } : undefined);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={`dropdown__item-icon dropdown__item-icon--${style.type}`}>
                            <span className="material-icons">{style.icon}</span>
                          </div>
                          <div className="dropdown__item-content">
                            <p className="dropdown__item-title">{notification.title}</p>
                            <p className="dropdown__item-message">{notification.body}</p>
                            <span className="dropdown__item-time">
                              {formatRelativeTime(notification.created_at)}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dropdown__empty">
                      <span className="material-icons">notifications_none</span>
                      <p>No notifications</p>
                    </div>
                  )}
                </div>
                <div className="dropdown__footer">
                  <button onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>
                    View all notifications
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* User Profile */}
        <div className="content-header__user">
          <button
            className="content-header__user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-label="User menu"
          >
            <div
              className={`content-header__avatar${isPaid ? ' content-header__avatar--paid' : ''}`}
              title={isPaid ? 'Paid account' : undefined}
              aria-label={isPaid ? 'Paid account' : undefined}
            >
              {user?.profile_photo_url ? (
                <img src={optimizedImageUrl(user.profile_photo_url, 96)} alt={userName} decoding="async" />
              ) : (
                <span className="content-header__avatar-initials">{getInitials(userName)}</span>
              )}
            </div>
            <div className="content-header__user-info">
              <span className="content-header__user-name">{userName}</span>
              <span className="content-header__user-role">{userRole}</span>
            </div>
          </button>

          {/* User Dropdown */}
          {showUserMenu && (
            <>
              <div
                className="content-header__dropdown-backdrop"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="content-header__dropdown content-header__dropdown--user">
                <button className="dropdown__menu-item" onClick={() => { setShowUserMenu(false); navigate('/account-settings'); }}>
                  <span className="material-icons">settings</span>
                  Account Settings
                </button>
                <button className="dropdown__menu-item" onClick={() => { setShowUserMenu(false); navigate('/help'); }}>
                  <span className="material-icons">help_outline</span>
                  Help & Support
                </button>
                <div className="dropdown__divider" />
                <button className="dropdown__menu-item dropdown__menu-item--danger" onClick={async () => {
                  await logout();
                  navigate('/login');
                }}>
                  <span className="material-icons">logout</span>
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Expanded Search */}
      {searchExpanded && (
        <div className="content-header__search-wrapper content-header__search-wrapper--mobile" ref={searchRef}>
          <form className="content-header__search-mobile" onSubmit={handleSearch}>
            <span className="material-icons content-header__search-icon">search</span>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchInput}
              className="content-header__search-input"
              autoFocus
            />
          </form>
          {renderSearchDropdown()}
        </div>
      )}
    </header>
  );
};

export default ContentHeader;
